import React, { useEffect, useRef, useState } from 'react';
import { Image, ImageStyle, StyleProp, TouchableOpacity, View, StyleSheet, ViewStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';

interface Props {
    photo?: string;
    setProfilePicture: React.Dispatch<React.SetStateAction<string>>;
    asAvatar?: boolean;
    disabled?: boolean;
    imageStyle?: StyleProp<ImageStyle>;
    emptyStyle?: StyleProp<ViewStyle>;
}

function ProfilePicture({ photo = '', setProfilePicture, asAvatar, disabled, imageStyle, emptyStyle }: Props) {
    const [image, setImage] = useState<string>('');
    const { styles, theme } = useStyles(styleSheet);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setImage(photo);
    }, [photo]);

    const pickImage = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        const uri = URL.createObjectURL(file);
        setImage(uri);
        setProfilePicture(uri);
        event.target.value = '';
    };

    function getImage() {
        return image ?
            <Image source={{ uri: image }} style={[styles.image, imageStyle]} />
            :
            <View style={[styles.empty, emptyStyle]}>
                <FontAwesomeIcon icon={faCircleUser} color={theme.colors.iconColor} fontSize={Number((StyleSheet.flatten(imageStyle) as ImageStyle)?.height) || 200} />
            </View>;
    }

    return (
        <View style={styles.container}>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />

            {asAvatar
                ? <>
                    {disabled
                        ? getImage()
                        : <TouchableOpacity onPress={pickImage}>
                            {getImage()}
                        </TouchableOpacity>
                    }
                </>
                : <>
                    {getImage()}

                    <TouchableOpacity disabled={disabled} onPress={pickImage} style={styles.button}>
                        <MyAppText style={styles.text}>{translate('updatePictureButtonLabel')}</MyAppText>
                    </TouchableOpacity>
                </>
            }
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
    },

    image: {
        width: 200,
        height: 200,
        marginBottom: 20,
        borderRadius: 100
    },

    empty: {
        marginBottom: 20,
    },

    button: {
        borderWidth: 0,
        borderRadius: 2,
        backgroundColor: theme.colors.primaryButton.background,
        width: 128,
        height: 40,
        justifyContent: 'center',
        alignItems: 'center'
    },

    text: {
        color: theme.colors.color
    }
}));

export default ProfilePicture;
